import { Divider } from '@eventbrite/eds-divider';
import { CrossChunky } from '@eventbrite/eds-iconography';
import { gettext } from '@eventbrite/i18n';
import { IconButton, Typography } from '@eventbrite/marmalade';
import classNames from 'classnames';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { CompletionType } from '../../../api/searchAutocomplete';
import { LocationInput } from '../../../containers/LocationInput';
import { SearchInput } from '../../../containers/SearchInput';
import {
    SearchDropdownContext,
    useLocationContext,
    useSearchInputContext,
} from '../../../context';
import { useRunSearch } from '../../../hooks';
import { DropdownOverlay } from '../DropdownOverlay';
import { SearchButton } from '../SearchButton';
import { SearchDropdown } from '../SearchDropdown';
import { AISearchModal } from '../SearchDropdown/AISearchCTA/AISearchModal';
import styles from './SearchBar.module.scss';

interface SearchInputContainerProps {
    handleSearchButtonClick: (e: React.MouseEvent<HTMLElement>) => void;
    isAISearchEnabled: boolean;
    currentLocation: string | undefined;
    showFullscreenMobileSearch?: boolean;
}
const SearchInputContainer = ({
    handleSearchButtonClick,
    isAISearchEnabled,
    currentLocation,
    showFullscreenMobileSearch = false,
}: SearchInputContainerProps) => {
    const { showDropdown, setShowDropdown, setShowOverlay } = useContext(
        SearchDropdownContext,
    );
    const [isAISearchOpen, setIsAISearchOpen] = useState(false);

    const handleSearchClick = useCallback(() => {
        if (isAISearchEnabled) {
            setIsAISearchOpen(true);
        } else {
            setShowDropdown(true);
            setShowOverlay(true);
        }
    }, [isAISearchEnabled, setIsAISearchOpen, setShowDropdown, setShowOverlay]);

    const handleSearchClickOutside = useCallback(
        (e?: React.MouseEvent<HTMLElement>) => {
            const target = e?.target as HTMLElement;
            if (!target) return;

            // Check if target or any ancestor has the excluded IDs
            const isSearchInput = target.closest('#search-autocomplete-input');
            const isMobileCloseButton = target.closest(
                '#mobile-search-close-button',
            );
            const isLocationAutocomplete = target.closest(
                '#location-autocomplete',
            );
            const isSearchDropdownOverlay = target.closest(
                '#search-dropdown-overlay',
            );
            const isMobileSearchHeader = target.closest(
                '#mobile-search-header',
            );

            if (isSearchInput) {
                return;
            } else if (
                isLocationAutocomplete ||
                isSearchDropdownOverlay ||
                isMobileSearchHeader ||
                isMobileCloseButton
            ) {
                setShowDropdown(false);
            } else {
                setShowDropdown(false);
                setShowOverlay(false);
            }
        },
        [setShowDropdown, setShowOverlay],
    );

    return (
        <div
            className={styles.searchLabelContainer}
            data-heap-id="seo-global-nav-search-bar-search-bar-click"
        >
            <div className={styles.searchLabel}>
                <SearchInput
                    variant="header"
                    dropdownVisible={showDropdown}
                    handleSearchClick={handleSearchClick}
                    handleBackButtonClick={handleSearchClickOutside}
                />
            </div>
            <div className={styles.mobileSearchBarRightSide}>
                <SearchButton onClick={handleSearchButtonClick} />
            </div>
            {showDropdown && (
                <SearchDropdown
                    onClickOutside={handleSearchClickOutside}
                    isAISearchEnabled={isAISearchEnabled}
                    openAISearch={() => setIsAISearchOpen(true)}
                    showFullscreenMobileSearch={showFullscreenMobileSearch}
                />
            )}
            <AISearchModal
                isAISearchEnabled={isAISearchEnabled}
                currentLocation={currentLocation}
                isOpen={isAISearchOpen}
                onClose={() => {
                    setIsAISearchOpen(false);
                    handleSearchClickOutside();
                }}
                isBotRequest={false}
            />
        </div>
    );
};

const LocationInputContainer = () => {
    const { setShowOverlay } = useContext(SearchDropdownContext);

    const handleLocationClick = useCallback(() => {
        setShowOverlay(true);
    }, [setShowOverlay]);

    const handleLocationSelect = () => {
        if (window?.innerWidth >= 660) {
            setShowOverlay(false);
        }
    };

    return (
        <div className={styles.dividerAndLocationContainer}>
            <div className={styles.dividerContainer}>
                <Divider direction="vertical" color="ui-500" />
            </div>
            <div
                className={styles.locationContainer}
                onClick={handleLocationClick}
            >
                <div className={styles.locationLabel}>
                    <LocationInput
                        onLocationSelect={handleLocationSelect}
                        variant="header"
                    />
                </div>
            </div>
        </div>
    );
};

const MobileSearchHeader = ({
    onOverlayClose,
}: {
    onOverlayClose: () => void;
}) => {
    return (
        <div id="mobile-search-header" className={styles.mobileSearchHeader}>
            <Typography variant="heading-sm" as="span" color="neutral-700">
                {gettext('Search')}
            </Typography>
            <IconButton
                aria-label={gettext('Close').toString()}
                onClick={onOverlayClose}
                icon={<CrossChunky aria-hidden="true" />}
                title={gettext('Close').toString()}
                id="mobile-search-close-button"
                className={styles.mobileSearchCloseButton}
            />
        </div>
    );
};

const SearchBar = ({ isAISearchEnabled }: { isAISearchEnabled: boolean }) => {
    const { showOverlay, setShowOverlay } = useContext(SearchDropdownContext);
    const { query, completionTypes } = useSearchInputContext();
    const handleSearch = useRunSearch();
    const location = useLocationContext();

    const showFullscreenMobileSearch = useMemo(() => {
        return completionTypes?.includes(CompletionType.VENUE);
    }, [completionTypes]);

    const onOverlayClose = useCallback(() => {
        setShowOverlay(false);
    }, [setShowOverlay]);

    const handleSearchButtonClick = useCallback(() => {
        handleSearch({
            search: { q: query || '' },
            analytics: { action: 'RunSearch', label: query },
        });
    }, [handleSearch, query]);

    const searchBarClassnames = classNames(styles.searchBarWithLocation, {
        [styles.overlayOpen]: showOverlay,
    });

    return (
        <>
            <div className={searchBarClassnames} data-testid="header-search">
                <Typography
                    variant="body-md"
                    as="span"
                    color="neutral-700"
                    className={styles.searchBarContent}
                >
                    <SearchInputContainer
                        handleSearchButtonClick={handleSearchButtonClick}
                        isAISearchEnabled={isAISearchEnabled}
                        currentLocation={location.location?.currentPlace}
                        showFullscreenMobileSearch={showFullscreenMobileSearch}
                    />
                    <LocationInputContainer />
                    <div className={styles.desktopSearchBarRightSide}>
                        <SearchButton onClick={handleSearchButtonClick} />
                    </div>
                </Typography>
                {showOverlay && showFullscreenMobileSearch && (
                    <MobileSearchHeader onOverlayClose={onOverlayClose} />
                )}
                <DropdownOverlay
                    domNodeId="root"
                    onClose={onOverlayClose}
                    isVisible={showOverlay}
                />
            </div>
        </>
    );
};

export default SearchBar;
